import { useMutation, useQuery } from 'react-query'
import auth from 'api'
import {
  parsePage,
  parseFilters,
  generateFilter,
  parseSearch,
} from 'helpers/api'
import { getUsers } from 'api/User/Users/queries'

//  --------------------------------------------------

export const getLudensoRemainings = async () => {
  const { data: ludensoRemainings } = await auth({
    method: 'get',
    url: `auth/school-features/ludenso/remaining`,
  })

  return ludensoRemainings
}

export const useLudensoRemainings = ({
  queryFilters,
  queryOptions = {},
} = {}) => {
  const { data, ...rest } = useQuery(
    queryFilters
      ? ['ludenso-remainings', queryFilters]
      : ['ludenso-remainings'],
    getLudensoRemainings,
    {
      staleTime: 1000 * 60 * 60,
      refetchOnWindowFocus: false,
      ...queryOptions,
    }
  )

  return { data, ...rest }
}

//  --------------------------------------------------

export const launchLudenso = async (payload) => {
  const data = await auth({
    method: 'post',
    url: `auth/school-features/ludenso/launch`,
    data: {
      ...payload,
    },
  }).catch((error) => {
    return error.response.data
  })
  return data
}

export const useLaunchLudenso = ({ queryOptions } = {}) => {
  return useMutation(launchLudenso, {
    ...queryOptions,
  })
}

// --------------------------------------------------

export const getLudensoUsers = async ({ queryKey }) => {
  const [, page, { filters = [], includeModels = [] } = {}] = queryKey

  let condition = ''
  condition = parsePage(page, condition)
  condition = parseFilters(filters, condition)

  const { data: ludensoUsers } = await auth({
    method: 'get',
    url: `auth/school-features/ludenso/users${condition}`,
  })

  let usersData = []
  let ludensoUserWithData = ludensoUsers

  if (includeModels?.includes('Users') && ludensoUsers?.items?.length) {
    const userIDs = ludensoUsers.items.map((usr) => usr.user_id)
    if (userIDs.length) {
      usersData = await getUsers({
        queryKey: [null, null, { users: userIDs }],
      })
    }
    if (usersData?.items?.length) {
      ludensoUserWithData = {
        ...ludensoUsers,
        items: ludensoUsers.items.map((item) => {
          const user = usersData.items.find((usr) => usr.id === item.user_id)
          return user ? { ...item, User: user } : item
        }),
      }
    }
  }

  return ludensoUserWithData
}

export const useLudensoUsers = (
  page,
  { queryFilters = {}, queryOptions = {} } = {}
) => {
  const paginationIsDisabled = page == null

  return useQuery(
    [
      'ludensoUsers',
      paginationIsDisabled ? null : page + 1,
      { ...queryFilters },
    ],
    getLudensoUsers,
    {
      keepPreviousData: !paginationIsDisabled,
      staleTime: paginationIsDisabled ? 0 : 1000 * 60 * 60,
      ...queryOptions,
    }
  )
}

export const searchTableLudensoUsers = async ({ queryKey }) => {
  const [, page, { filters = [], includeModels = [], searchModels = {} } = {}] =
    queryKey

  let foundUsers
  let userIds = []
  let ludensoFilters = [...filters]

  if (searchModels['Users']) {
    let userSearch = parseSearch('', searchModels, 'Users')
    foundUsers = await getUsers({
      queryKey: [
        null,
        null,
        {
          search: userSearch,
        },
      ],
    })
    userIds = foundUsers?.items?.map((usr) => usr.id) || []

    ludensoFilters = generateFilter(ludensoFilters, userIds, 'user_id')
  }

  if (searchModels['Users'] && foundUsers?.total_items === 0) {
    return { items: [], total_items: 0 }
  }

  if (searchModels['LudensoUsers']) {
    Object.entries(searchModels.LudensoUsers).forEach(([key, val]) => {
      const parsed = Object.fromEntries(new URLSearchParams(val))
      const value =
        parsed[key + '[like]'] || parsed[key + '[eq]'] || parsed[key]
      if (value) {
        ludensoFilters.push({ by: key, value })
      }
    })
  }

  let includeModelsLudenso = [...includeModels]
  if (searchModels['Users']) {
    includeModelsLudenso = includeModelsLudenso.filter((m) => m !== 'Users')
  }

  const ludensoUsers = await getLudensoUsers({
    queryKey: [
      null,
      page,
      {
        filters: ludensoFilters,
        includeModels: includeModelsLudenso,
      },
    ],
  })

  let ludensoUserWithData = ludensoUsers
  if (
    includeModels?.includes('Users') &&
    foundUsers?.items?.length &&
    ludensoUsers?.items?.length
  ) {
    ludensoUserWithData = {
      ...ludensoUsers,
      items: ludensoUsers.items.map((item) => {
        const user = foundUsers.items.find((usr) => usr.id === item.user_id)
        return user ? { ...item, User: user } : item
      }),
    }
  }

  return ludensoUserWithData
}

export const useSearchLudensoUsers = (
  page,
  { queryFilters = {}, queryOptions = {} } = {}
) => {
  const paginationIsDisabled = page == null

  return useQuery(
    [
      'searchLudensoUsers',
      paginationIsDisabled ? null : page + 1,
      { ...queryFilters },
    ],
    searchTableLudensoUsers,
    {
      keepPreviousData: !paginationIsDisabled,
      staleTime: 0,
      refetchOnWindowFocus: false,
      ...queryOptions,
    }
  )
}


export const createLudensoUser = async (payload) => {
  const { data } = await auth({
    method: 'post',
    url: `auth/school-features/ludenso/users`,
    data: payload,
  })
  return data
}

export const useCreateLudensoUser = ({ queryOptions } = {}) => {
  return useMutation(createLudensoUser, {
    ...queryOptions,
  })
}

export const updateLudensoUser = async ({ id, payload }) => {
  const { data } = await auth({
    method: 'put',
    url: `auth/school-features/ludenso/users/${id}`,
    data: payload,
  })
  return data
}

export const useUpdateLudensoUser = ({ queryOptions } = {}) => {
  return useMutation(updateLudensoUser, {
    ...queryOptions,
  })
}

